/**
 * Почему запись закрыта как `not_applicable` — один список на конвейер и на
 * интерфейс.
 *
 * До этого списка было два, и они разъехались молча. Причину писала ровно одна
 * ветка конвейера — отказ гибридного источника аудио, — а интерфейс держал
 * собственный набор из тех же трёх строк. Две самые частые ветки закрытия
 * («в активности CRM нет файла», «пропущенный звонок») не писали причину
 * вовсе, и карточка записи падала в общий текст статуса: клиент видел значок
 * «записи нет» и подпись, из которой следовало обратное.
 *
 * Общий список этого не чинит сам по себе — но делает расхождение видимым:
 * тест `record-halt-reasons.i18n.test.ts` требует текста на КАЖДУЮ причину в
 * четырёх локалях, поэтому новая причина без копии не доедет до прода тихо.
 *
 * Список ИСЧЕРПЫВАЮЩИЙ, и это его главное свойство. Первая редакция покрывала
 * только звонковые ветки, а четыре закрытия переписки (`chat_*`) продолжали
 * писать в ту же колонку английскую прозу: `isNotApplicableReason` её не
 * признавал, карточка тихо падала в общий текст — тот самый безмолвный откат,
 * ради устранения которого реестр и заводился, — а i18n-тест, идущий по этому
 * списку, увидеть такое не мог в принципе. Ветка, пишущая `error_message` мимо
 * реестра, — не «пока без копии», а восстановленный дефект.
 *
 * Значения совпадают со строкой в `records.error_message`; переименование
 * любой из них — миграция уже записанных строк, а не правка константы.
 */

/** Пропущенный вызов: разговора не было, записывать нечего. */
export const REASON_MISSED_CALL = 'missed_call';
/** CRM зарегистрировала звонок, но файла в активности так и не появилось. */
export const REASON_NO_RECORDING_IN_CRM = 'no_recording_in_crm';
/** Оператор телефонии подтвердил: этот звонок он не записывал. */
export const REASON_NO_RECORDING_AT_SOURCE = 'no_recording_at_source';
/** За сутки у оператора не нашлось подходящего звонка. */
export const REASON_SOURCE_NOT_FOUND = 'source_not_found';
/** После сужения у оператора всё ещё подходит больше одного звонка. */
export const REASON_AMBIGUOUS_MATCH = 'ambiguous_match';
/** Источник разговоров отключён клиентом — забирать запись стало неоткуда. */
export const REASON_SOURCE_DISCONNECTED = 'source_disconnected';
/** Переписка не закрылась за семь суток ожидания. */
export const REASON_CHAT_TIMEOUT = 'chat_timeout';
/** Активности переписки в CRM не нашлось. */
export const REASON_CHAT_NOT_FOUND = 'chat_not_found';
/** Переписка закрылась, не набрав ни одного сообщения. */
export const REASON_CHAT_NO_MESSAGES = 'chat_no_messages';
/**
 * Переписка длиннее предела анализа (`MAX_ANALYZED_CHAT_BYTES`, #1489):
 * сохранена и видна, но не анализируется и не оплачивается. Не «короткая» —
 * `isShortCall` у неё не ставится, и интерфейс говорит своё.
 */
export const REASON_CHAT_TOO_LONG = 'chat_too_long';

/**
 * Все причины, которым интерфейс обязан уметь дать объяснение.
 *
 * Порядок — от самой частой к самой редкой; на поведение не влияет, но
 * подсказывает, что читать первым.
 */
export const NOT_APPLICABLE_REASONS = [
  REASON_MISSED_CALL,
  REASON_NO_RECORDING_IN_CRM,
  REASON_NO_RECORDING_AT_SOURCE,
  REASON_SOURCE_NOT_FOUND,
  REASON_AMBIGUOUS_MATCH,
  REASON_SOURCE_DISCONNECTED,
  REASON_CHAT_TIMEOUT,
  REASON_CHAT_NOT_FOUND,
  REASON_CHAT_NO_MESSAGES,
  REASON_CHAT_TOO_LONG,
] as const;

export type NotApplicableReason = (typeof NOT_APPLICABLE_REASONS)[number];

/**
 * Причины, которые могут ПЕРЕСТАТЬ действовать И которые нам есть чем снять.
 *
 * Остальные невосстановимы по природе: пропущенного звонка не было, файла у
 * оператора нет и не появится, переписка закрылась пустой. `source_disconnected`
 * — про обстоятельство снаружи: источник переподключают, и запись можно
 * забрать заново (`awaitMedia`). Разговор при этом пригоден — помешала
 * интеграция, а не содержимое (#460).
 *
 * Список УЖЕ, чем `NOT_APPLICABLE_REASONS`, и таким должен остаться: причина
 * попадает сюда только если названо, ЧЕМ именно мы её снимаем. Иначе интерфейс
 * предложит ход, который ничего не изменит, — а это хуже молчания.
 *
 * **`chat_timeout` сюда НЕ входит, хотя обстоятельство у него тоже меняется**
 * (переписка закрывается позже семи суток). Снять его нечем: воркфлоу
 * дочитывает сессию из CRM по `chatActivityId` + `installationId`, а на
 * стороне edge-api первого нет — то же ограничение уже записано в
 * `utils/record-start.ts` («Чат без текста запускать нечем»). Схема запуска
 * это ловит явно: `awaitMedia` при `channel !== 'call'` отвергается
 * (`pipelineStartRequestSchema`), то есть чатовый повтор молча не сработал бы,
 * успев при этом вывести запись из закрытого состояния. Найдено ревью Codex на
 * #567; продолжение — #568.
 */
export const RECOVERABLE_HALT_REASONS = [REASON_SOURCE_DISCONNECTED] as const;

export type RecoverableHaltReason = (typeof RECOVERABLE_HALT_REASONS)[number];

/**
 * Снимет ли повторная попытка забрать медиа эту причину.
 *
 * Отвечает на строку из `records.error_message` как есть: вызывающему не нужно
 * сперва проверять `isNotApplicableReason` — чужая строка просто даёт `false`.
 */
export function isRecoverableHaltReason(
  value: string | null | undefined
): value is RecoverableHaltReason {
  return (
    typeof value === 'string' &&
    (RECOVERABLE_HALT_REASONS as readonly string[]).includes(value)
  );
}

/** Знает ли интерфейс, как объяснить эту строку из `error_message`. */
export function isNotApplicableReason(
  value: string | null | undefined
): value is NotApplicableReason {
  return (
    typeof value === 'string' &&
    (NOT_APPLICABLE_REASONS as readonly string[]).includes(value)
  );
}
